// HTML view of one Consumer Voice audit (product or brand). Laid out per the
// Consumer Voice Data Source Strategy: the three-index strip (Consumer
// Voice, Consumer Demand, Content Opportunity), the objective-aligned
// scores (Trust / Education / Hype), search demand, ranked content
// opportunities, then the evidence — verbatim quotes always link to their
// source (Reddit thread, YouTube video, retailer review or Q&A).
import React from 'react';
import dayjs from 'dayjs';
import {
  ConsumerVoiceAudit,
  ConsumerVoiceBrandReport,
  ConsumerVoiceProductReport,
  CvContentOpportunity,
  CvDemand,
  CvQuote,
  CvReportMeta,
  cvDiscoveredLine,
  CV_OBJECTIVE_SCORES,
  CV_OPPORTUNITY_TYPE_LABELS,
  cvSourceLabel,
} from '../../types/consumerVoice';
import { ConsumerVoiceDemandChart } from './ConsumerVoiceDemandChart';

const scoreTone = (score: number | null | undefined) => {
  if (score == null) return 'bg-surface-2 text-text-2';
  if (score >= 70) return 'bg-[#E8F5E9] text-[#2E7D32]';
  if (score >= 45) return 'bg-tint text-accent-text';
  return 'bg-[#FBEFEF] text-[#c62828]';
};

const sentimentTone = (s: string) => {
  if (s === 'positive') return 'text-[#2E7D32]';
  if (s === 'negative') return 'text-[#c62828]';
  return 'text-accent-text';
};

const TREND_LABEL: Record<string, string> = {
  breakout: 'Breakout',
  rising: 'Rising',
  stable: 'Stable',
  falling: 'Falling',
  none: 'No search interest',
};

const fmt = (n: number | null | undefined, digits = 0) =>
  n == null ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: digits });

const pct = (v: number | null | undefined) => (v == null ? '—' : `${Math.round(v * 100)}%`);

const trendTone = (direction: string | null | undefined) => {
  if (direction === 'rising' || direction === 'breakout') return 'bg-[#E8F5E9] text-[#2E7D32]';
  if (direction === 'falling') return 'bg-[#FBEFEF] text-[#c62828]';
  return '';
};

const risingSuffix = (q: { breakout?: boolean; value: number | null }) => {
  if (q.breakout) return ' · breakout';
  if (q.value != null) return ` · +${q.value}%`;
  return '';
};

// "Momentum 80 · Discussion 49" from a components block, nulls skipped.
const componentsLine = (block?: { components: Record<string, number | null> } | null, labels: Record<string, string> = {}) => {
  if (!block || !block.components) return '';
  return Object.keys(block.components)
    .filter((k) => block.components[k] != null)
    .map((k) => `${labels[k] || k.replace(/_/g, ' ')} ${block.components[k]}`)
    .join(' · ');
};

function IndexCard({
  label,
  value,
  caption,
  placeholder,
  small,
}: {
  label: string;
  value: number | null | undefined;
  caption: string;
  placeholder?: string;
  small?: boolean;
}) {
  return (
    <div className={`flex-1 min-w-[180px] border border-border-token rounded-xl ${small ? 'p-3' : 'p-4'} bg-surface`}>
      <div className="text-[11px] font-bold uppercase tracking-[0.5px] text-muted mb-2">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className={`${small ? 'text-[22px]' : 'text-[28px]'} font-bold leading-none rounded-lg px-2 py-1 ${scoreTone(value)}`}>
          {value == null ? '—' : value}
        </span>
        <span className="text-[11px] text-muted">/ 100</span>
      </div>
      <div className="text-[12px] text-text-2 mt-2 leading-snug">{value == null && placeholder ? placeholder : caption}</div>
    </div>
  );
}

function SourceLink({ url, label = 'source' }: { url?: string | null; label?: string }) {
  if (!url) return null;
  return (
    <a href={url} target="_blank" rel="noopener noreferrer" className="not-italic text-[12px] text-accent-text hover:underline">
      {label} ↗
    </a>
  );
}

function QuoteList({ quotes }: { quotes: CvQuote[] | undefined }) {
  if (!quotes || !quotes.length) return null;
  return (
    <ul className="mt-1.5 flex flex-col gap-1">
      {quotes.slice(0, 3).map((q, i) => (
        <li key={i} className="text-[12px] text-text-2 italic pl-3 border-l-2 border-border-token">
          “{q.text}” <SourceLink url={q.url} />
        </li>
      ))}
    </ul>
  );
}

function Section({ title, children, count }: { title: string; children: React.ReactNode; count?: number }) {
  return (
    <section className="mt-7">
      <h3 className="text-[13px] font-bold uppercase tracking-[0.5px] text-text-1 mb-2.5">
        {title}
        {typeof count === 'number' ? <span className="ml-2 text-muted font-semibold">{count}</span> : null}
      </h3>
      {children}
    </section>
  );
}

const Empty = ({ text }: { text: string }) => <p className="text-[12px] text-muted">{text}</p>;

const Chip = ({ children, tone = '' }: { children: React.ReactNode; tone?: string }) => (
  <span className={`text-[11px] font-semibold rounded-full px-2.5 py-1 ${tone || 'bg-surface-2 text-text-2'}`}>{children}</span>
);

// The three objective-aligned scores. Rendered for product and brand rows.
export function ObjectiveScoreRow({ audit, meta }: { audit: ConsumerVoiceAudit; meta?: CvReportMeta | null }) {
  const blocks = meta && meta.objective_scores;
  const anyPresent = CV_OBJECTIVE_SCORES.some((o) => audit[o.key] != null);
  if (!anyPresent && !blocks) return null;
  const captionFor = (key: 'trust' | 'education' | 'hype', fallback: string) =>
    blocks && blocks[key] ? componentsLine(blocks[key]) || fallback : fallback;
  return (
    <div className="mt-3">
      <div className="text-[11px] font-bold uppercase tracking-[0.5px] text-muted mb-2">Objective alignment</div>
      <div className="flex flex-wrap gap-3">
        {CV_OBJECTIVE_SCORES.map((o) => (
          <IndexCard
            key={o.key}
            small
            label={`${o.label} · ${o.objective}`}
            value={audit[o.key]}
            caption={captionFor(o.key.replace('_score', '') as 'trust' | 'education' | 'hype', o.caption)}
            placeholder={`Not enough signal for a ${o.label.toLowerCase()} read yet.`}
          />
        ))}
      </div>
    </div>
  );
}

function DemandSection({ demand, meta }: { demand: CvDemand; meta?: CvReportMeta | null }) {
  const tr = demand.trends;
  const kp = demand.keyword_planner;
  const sc = demand.search_console;
  const nothing = !tr && !kp && !sc && !demand.youtube && !demand.retailer;
  return (
    <Section title="Search demand">
      {demand.keywords && demand.keywords.length ? (
        <p className="text-[12px] text-muted mb-3">
          Terms analyzed: {demand.keywords.map((k, i) => <Chip key={i}>{k}</Chip>).reduce<React.ReactNode[]>((acc, el, i) => (i ? [...acc, ' ', el] : [el]), [])}
        </p>
      ) : null}
      {nothing ? <Empty text="No search-demand sources ran for this analysis." /> : null}

      {tr ? (
        <div className="border border-border-token rounded-lg p-3 mb-3">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-text-1 text-[13px]">Google Trends · {tr.primary || demand.keywords[0]}</span>
            {tr.trend_direction ? (
              <Chip tone={trendTone(tr.trend_direction)}>{TREND_LABEL[tr.trend_direction] || tr.trend_direction}</Chip>
            ) : null}
            {tr.query_growth_pct != null ? (
              <span className="text-[12px] text-text-2">
                {tr.query_growth_pct >= 0 ? '+' : ''}
                {tr.query_growth_pct}% last 90 days vs prior
              </span>
            ) : null}
            {tr.partial ? <span className="text-[11px] text-muted">(partial data)</span> : null}
          </div>
          {tr.headline_term && tr.primary && tr.headline_term !== tr.primary ? (
            <p className="text-[11.5px] text-muted mt-1 mb-0">
              &ldquo;{tr.primary}&rdquo; has no measurable search interest on its own — momentum is read from &ldquo;{tr.headline_term}&rdquo;.
            </p>
          ) : null}
          {tr.interest_over_time && tr.interest_over_time.length > 1 ? <ConsumerVoiceDemandChart trends={tr} /> : null}
          {tr.comparison_share && Object.keys(tr.comparison_share).length > 1 ? (
            <div className="mt-2">
              <div className="text-[11px] text-muted mb-1">Share of search interest across the compared terms</div>
              <div className="flex flex-col gap-1">
                {Object.keys(tr.comparison_share).map((k) => {
                  const share = tr.comparison_share ? tr.comparison_share[k] : null;
                  return (
                    <div key={k} className="flex items-center gap-2 text-[12px]">
                      <span className="w-[180px] truncate text-text-2">{k}</span>
                      <div className="flex-1 h-2 rounded-full bg-surface-2 overflow-hidden">
                        <div className="h-2 bg-accent-text/60" style={{ width: `${Math.round((share || 0) * 100)}%` }} />
                      </div>
                      <span className="w-10 text-right text-text-2">{pct(share)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 mt-3">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.5px] text-muted mb-1">Rising searches</div>
              {tr.related_queries && tr.related_queries.rising.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {tr.related_queries.rising.slice(0, 12).map((q, i) => (
                    <a key={i} href={q.link || undefined} target="_blank" rel="noopener noreferrer" className="no-underline">
                      <Chip tone={q.breakout ? 'bg-[#E8F5E9] text-[#2E7D32]' : ''}>
                        {q.query}
                        {risingSuffix(q)}
                      </Chip>
                    </a>
                  ))}
                </div>
              ) : (
                <Empty text="None." />
              )}
            </div>
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.5px] text-muted mb-1">Top related searches</div>
              {tr.related_queries && tr.related_queries.top.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {tr.related_queries.top.slice(0, 12).map((q, i) => (
                    <Chip key={i}>{q.query}</Chip>
                  ))}
                </div>
              ) : (
                <Empty text="None." />
              )}
            </div>
          </div>
          {tr.geo_interest && tr.geo_interest.length ? (
            <p className="text-[12px] text-text-2 mt-3">
              Strongest regions: {tr.geo_interest.slice(0, 5).map((g) => g.region).join(', ')}
            </p>
          ) : null}
        </div>
      ) : null}

      {kp ? (
        <div className="border border-border-token rounded-lg p-3 mb-3">
          <div className="font-semibold text-text-1 text-[13px]">Google Keyword Planner · average monthly US searches</div>
          <div className="flex flex-wrap gap-4 mt-2 text-[12px] text-text-2">
            <span>
              Product searches <strong className="text-text-1">{fmt(kp.search_volume)}</strong>/mo
            </span>
            <span>
              Brand <strong className="text-text-1">{fmt(kp.search_volume_brand)}</strong>
            </span>
            <span>
              Category <strong className="text-text-1">{fmt(kp.search_volume_category)}</strong>
            </span>
            {kp.query_growth_pct != null ? <span>Last 3 months vs prior {kp.query_growth_pct >= 0 ? '+' : ''}{kp.query_growth_pct}%</span> : null}
            {kp.competition_index != null ? <span>Competition {kp.competition_index}/100</span> : null}
          </div>
          {kp.related_keywords && kp.related_keywords.length ? (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {kp.related_keywords.slice(0, 15).map((k, i) => (
                <Chip key={i}>
                  {k.text} · {fmt(k.avg_monthly_searches)}
                </Chip>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {sc ? (
        <div className="border border-border-token rounded-lg p-3 mb-3">
          <div className="font-semibold text-text-1 text-[13px]">
            Google Search Console · {sc.domain}
            {sc.window ? <span className="text-[11px] text-muted font-normal"> · {sc.window.startDate} to {sc.window.endDate}</span> : null}
          </div>
          <div className="flex flex-wrap gap-4 mt-2 text-[12px] text-text-2">
            <span>
              Impressions <strong className="text-text-1">{fmt(sc.impressions)}</strong>
            </span>
            <span>
              Clicks <strong className="text-text-1">{fmt(sc.clicks)}</strong>
            </span>
            <span>CTR {pct(sc.ctr)}</span>
            <span>Avg position {fmt(sc.position, 1)}</span>
          </div>
          {sc.top_queries && sc.top_queries.length ? (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {sc.top_queries.slice(0, 15).map((q, i) => (
                <Chip key={i}>
                  {q.query} · {fmt(q.impressions)}
                </Chip>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}

      {demand.youtube || demand.retailer ? (
        <p className="text-[12px] text-text-2">
          {demand.youtube ? `YouTube: ${demand.youtube.videos} review videos, ${fmt(demand.youtube.total_views)} views, ${demand.youtube.comments} comments read. ` : ''}
          {demand.retailer
            ? `Retailer pages: ${fmt(demand.retailer.review_count)} reviews${demand.retailer.avg_rating != null ? ` averaging ${demand.retailer.avg_rating}/5` : ''}${demand.retailer.question_count != null ? `, ${demand.retailer.question_count} shopper questions (${demand.retailer.unanswered_question_count ?? 0} unanswered)` : ''}.`
            : ''}
        </p>
      ) : null}

      {meta && meta.demand && meta.demand.components ? (
        <p className="text-[11px] text-muted mt-2">
          Demand Index inputs: {componentsLine(meta.demand, { first_party_intent: 'first-party intent' }) || 'none'}
          {meta.demand.volume_source ? ` (volume from ${meta.demand.volume_source.replace('_', ' ')})` : ''}
        </p>
      ) : null}
    </Section>
  );
}

function OpportunityRows({ rows, showProducts }: { rows: CvContentOpportunity[]; showProducts?: boolean }) {
  return (
    <div className="flex flex-col gap-2">
      {rows.map((o) => (
        <div key={o.key} className="border border-border-token rounded-lg p-3">
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-[44px]">
              <div className={`text-[16px] font-bold leading-none rounded-md px-1.5 py-1 text-center ${scoreTone(o.score)}`}>{o.score}</div>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-text-1 text-[13px]">{o.title}</span>
                <Chip>{CV_OPPORTUNITY_TYPE_LABELS[o.type] || o.type}</Chip>
                {o.sources && o.sources.length ? <span className="text-[11px] text-muted">{o.sources.map((s) => s.replace(/_/g, ' ')).join(', ')}</span> : null}
              </div>
              {showProducts && o.products && o.products.length ? <div className="text-[12px] text-text-2 mt-0.5">{o.products.join(', ')}</div> : null}
              {o.evidence && o.evidence.length ? (
                <ul className="mt-1.5 flex flex-col gap-1">
                  {o.evidence.slice(0, 3).map((e, i) => (
                    <li key={i} className="text-[12px] text-text-2 italic pl-3 border-l-2 border-border-token">
                      {e.text}
                      {e.demand_kind === 'volume' && e.demand_value != null ? <span className="not-italic text-muted"> · {fmt(e.demand_value)}/mo</span> : null}
                      {e.demand_kind === 'impressions' && e.demand_value != null ? <span className="not-italic text-muted"> · {fmt(e.demand_value)} impressions</span> : null}{' '}
                      <SourceLink url={e.url} />
                    </li>
                  ))}
                </ul>
              ) : null}
              <div className="text-[11px] text-muted mt-1">
                demand {o.components.demand} · frequency {o.components.frequency} · page gap {o.components.gap}
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ProductBody({ audit, report }: { audit: ConsumerVoiceAudit; report: ConsumerVoiceProductReport }) {
  const sourcesUsed = (report.meta && report.meta.sources_used) || [];
  const ran = sourcesUsed.filter((s) => !s.skipped);
  const skipped = sourcesUsed.filter((s) => s.skipped);
  return (
    <>
      <p className="text-[14px] text-text-1 leading-relaxed mt-6">{report.summary}</p>
      <p className="text-[12px] text-muted mt-2">
        Coverage: <strong className="text-text-2">{report.coverage}</strong> · {report.relevant_mention_count} relevant mentions
        {report.discussion_volume ? ` · discussion volume ${report.discussion_volume}` : ''}
        {audit.subscores && audit.subscores.confidence != null ? ` · confidence ${Math.round(audit.subscores.confidence * 100)}%` : ''}
        {ran.length ? ` · sources: ${ran.map((s) => `${s.key.replace(/_/g, ' ')} (${s.items || 0}${s.error ? ', error' : ''})`).join(', ')}` : ''}
        {skipped.length ? ` · not run: ${skipped.map((s) => s.key.replace(/_/g, ' ')).join(', ')}` : ''}
      </p>
      {cvDiscoveredLine(report.meta) ? <p className="text-[12px] text-muted mt-1">{cvDiscoveredLine(report.meta)}</p> : null}
      {report.understanding && report.understanding.clarity_note && report.understanding.clarity_note.toLowerCase() !== 'none' ? (
        <p className="text-[12px] text-text-2 mt-1">
          What shoppers get wrong: {report.understanding.clarity_note} ({report.understanding.confusion_share}% of mentions show confusion)
        </p>
      ) : null}

      {report.demand ? <DemandSection demand={report.demand} meta={report.meta} /> : null}

      {report.content_opportunities ? (
        <Section title="Content opportunities" count={report.content_opportunities.length}>
          {report.content_opportunities.length ? (
            <OpportunityRows rows={report.content_opportunities} />
          ) : (
            <Empty text="Nothing shoppers ask or search for went unanswered in the data." />
          )}
          {report.meta && report.meta.opportunity && report.meta.opportunity.components ? (
            <p className="text-[11px] text-muted mt-2">
              Opportunity Index inputs: {componentsLine(report.meta.opportunity) || 'none'}
            </p>
          ) : null}
        </Section>
      ) : null}

      <Section title="Themes" count={report.themes.length}>
        {report.themes.length ? (
          <div className="flex flex-col gap-3">
            {report.themes.map((t, i) => (
              <div key={i} className="border border-border-token rounded-lg p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-text-1 text-[13px]">{t.theme}</span>
                  <span className={`text-[11px] font-bold uppercase ${sentimentTone(t.sentiment)}`}>{t.sentiment}</span>
                  <span className="text-[11px] text-muted">{t.frequency} frequency</span>
                </div>
                <p className="text-[12.5px] text-text-2 mt-1">{t.insight}</p>
                <QuoteList quotes={t.quotes} />
              </div>
            ))}
          </div>
        ) : (
          <Empty text="No themes — the scan found too little relevant discussion." />
        )}
      </Section>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
        <Section title="Praise" count={report.praise.length}>
          {report.praise.length ? (
            report.praise.map((p, i) => (
              <div key={i} className="mb-3">
                <div className="text-[13px] text-text-1">{p.text}</div>
                <QuoteList quotes={p.quotes} />
              </div>
            ))
          ) : (
            <Empty text="None found." />
          )}
        </Section>
        <Section title="Pain points" count={report.pain_points.length}>
          {report.pain_points.length ? (
            report.pain_points.map((p, i) => (
              <div key={i} className="mb-3">
                <div className="text-[13px] text-text-1">
                  {p.text} <span className="text-[11px] text-muted">[{p.severity}]</span>
                </div>
                <QuoteList quotes={p.quotes} />
              </div>
            ))
          ) : (
            <Empty text="None found." />
          )}
        </Section>
      </div>

      <Section title="Purchase objections" count={report.objections.length}>
        {report.objections.length ? (
          report.objections.map((o, i) => (
            <div key={i} className="mb-3">
              <div className="text-[13px] text-text-1">
                {o.text} {o.category ? <span className="text-[11px] text-muted">[{o.category.replace(/_/g, ' ')}]</span> : null}
              </div>
              <QuoteList quotes={o.quotes} />
            </div>
          ))
        ) : (
          <Empty text="None found." />
        )}
      </Section>

      <Section title="Unanswered shopper questions" count={report.unanswered_questions.length}>
        {report.unanswered_questions.length ? (
          <ul className="list-disc pl-5 flex flex-col gap-1">
            {report.unanswered_questions.map((q, i) => (
              <li key={i} className="text-[13px] text-text-1">
                {q.question} {q.topic ? <span className="text-[11px] text-muted">[{q.topic.replace(/_/g, ' ')}]</span> : null} <SourceLink url={q.url} />
              </li>
            ))}
          </ul>
        ) : (
          <Empty text="None captured." />
        )}
      </Section>

      {report.comparisons.length ? (
        <Section title="Compared against" count={report.comparisons.length}>
          <ul className="flex flex-col gap-1">
            {report.comparisons.map((c, i) => (
              <li key={i} className="text-[13px] text-text-1">
                <strong>{c.competitor}</strong> <span className="text-text-2">— {c.context}</span>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      {(report.personas.length || report.use_cases.length) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
          <Section title="Personas">
            {report.personas.length ? (
              <div className="flex flex-wrap gap-1.5">
                {report.personas.map((p, i) => (
                  <Chip key={i}>{p}</Chip>
                ))}
              </div>
            ) : (
              <Empty text="None." />
            )}
          </Section>
          <Section title="Use cases">
            {report.use_cases.length ? (
              <div className="flex flex-wrap gap-1.5">
                {report.use_cases.map((p, i) => (
                  <Chip key={i}>{p}</Chip>
                ))}
              </div>
            ) : (
              <Empty text="None." />
            )}
          </Section>
        </div>
      )}

      {report.copy_bank.length ? (
        <Section title="Customer language copy bank" count={report.copy_bank.length}>
          <ul className="flex flex-col gap-1">
            {report.copy_bank.map((c, i) => (
              <li key={i} className="text-[12.5px] text-text-1 italic">
                “{c.phrase}” <SourceLink url={c.url} />
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      <Section title="Sources" count={report.sources.length}>
        {report.sources.length ? (
          <ol className="list-decimal pl-5 flex flex-col gap-0.5">
            {report.sources.map((s, i) => (
              <li key={i} className="text-[12px] text-text-2">
                {cvSourceLabel(s) ? `${cvSourceLabel(s)} — ` : ''}
                <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-accent-text hover:underline">
                  {s.title || s.url}
                </a>
              </li>
            ))}
          </ol>
        ) : (
          <Empty text="No sources — the scan returned no usable discussion." />
        )}
      </Section>
    </>
  );
}

const cell = (v: number | null | undefined) => (
  <span className={`inline-block rounded-md px-1.5 py-0.5 font-bold ${scoreTone(v)}`}>{v == null ? '—' : v}</span>
);

function BrandBody({ report, onOpenProduct }: { report: ConsumerVoiceBrandReport; onOpenProduct?: (auditId: number) => void }) {
  const hasIndices = report.per_product.some((p) => p.demand_index != null || p.opportunity_index != null || p.trust_score != null);
  return (
    <>
      <p className="text-[14px] text-text-1 leading-relaxed mt-6">{report.summary}</p>
      <p className="text-[12px] text-muted mt-2">
        {report.products_with_data} of {report.products_analyzed} analyzed products had usable consumer discussion.
      </p>

      <Section title="Products" count={report.per_product.length}>
        <div className="overflow-x-auto">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.5px] text-muted">
                <th className="py-1.5 pr-3">Product</th>
                <th className="py-1.5 pr-3">Score</th>
                {hasIndices ? (
                  <>
                    <th className="py-1.5 pr-3">Demand</th>
                    <th className="py-1.5 pr-3">Opportunity</th>
                    <th className="py-1.5 pr-3">Trust</th>
                    <th className="py-1.5 pr-3">Edu.</th>
                    <th className="py-1.5 pr-3">Hype</th>
                  </>
                ) : null}
                <th className="py-1.5 pr-3">Coverage</th>
                <th className="py-1.5">Read</th>
              </tr>
            </thead>
            <tbody>
              {report.per_product.map((p) => (
                <tr key={p.audit_id} className="border-t border-border-token align-top">
                  <td className="py-2 pr-3 text-text-1 font-semibold">
                    {onOpenProduct ? (
                      <button type="button" className="text-left hover:underline" onClick={() => onOpenProduct(p.audit_id)}>
                        {p.title}
                      </button>
                    ) : (
                      p.title
                    )}
                    {p.trend_direction && p.trend_direction !== 'stable' && p.trend_direction !== 'none' ? (
                      <div className="text-[11px] text-muted font-normal">search {TREND_LABEL[p.trend_direction]?.toLowerCase() || p.trend_direction}</div>
                    ) : null}
                  </td>
                  <td className="py-2 pr-3">{cell(p.overall_score)}</td>
                  {hasIndices ? (
                    <>
                      <td className="py-2 pr-3">{cell(p.demand_index)}</td>
                      <td className="py-2 pr-3">{cell(p.opportunity_index)}</td>
                      <td className="py-2 pr-3">{cell(p.trust_score)}</td>
                      <td className="py-2 pr-3">{cell(p.education_score)}</td>
                      <td className="py-2 pr-3">{cell(p.hype_score)}</td>
                    </>
                  ) : null}
                  <td className="py-2 pr-3 text-text-2">{p.coverage}</td>
                  <td className="py-2 text-text-2">{p.one_line || p.summary || (p.status === 'error' ? 'Analysis failed' : '—')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {report.top_opportunities && report.top_opportunities.length ? (
        <Section title="Top content opportunities" count={report.top_opportunities.length}>
          <OpportunityRows rows={report.top_opportunities} showProducts />
        </Section>
      ) : null}

      <Section title="Cross-product themes" count={report.cross_product_themes.length}>
        {report.cross_product_themes.length ? (
          <div className="flex flex-col gap-3">
            {report.cross_product_themes.map((t, i) => (
              <div key={i} className="border border-border-token rounded-lg p-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-text-1 text-[13px]">{t.theme}</span>
                  <span className={`text-[11px] font-bold uppercase ${sentimentTone(t.sentiment)}`}>{t.sentiment}</span>
                  <span className="text-[11px] text-muted">{t.products.join(', ')}</span>
                </div>
                <p className="text-[12.5px] text-text-2 mt-1">{t.insight}</p>
              </div>
            ))}
          </div>
        ) : (
          <Empty text="No theme recurred across products." />
        )}
      </Section>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6">
        {[
          ['Strongest praise', report.strongest_praise],
          ['Recurring pain points', report.recurring_pain_points],
          ['Recurring questions', report.recurring_questions],
        ].map(([title, items]) => (
          <Section key={title as string} title={title as string} count={(items as string[]).length}>
            {(items as string[]).length ? (
              <ul className="list-disc pl-5 flex flex-col gap-1">
                {(items as string[]).map((x, i) => (
                  <li key={i} className="text-[13px] text-text-1">{x}</li>
                ))}
              </ul>
            ) : (
              <Empty text="None." />
            )}
          </Section>
        ))}
      </div>
    </>
  );
}

export function ConsumerVoiceReportView({
  audit,
  onCreateStrategy,
  createStrategyLabel = 'Create strategy',
  onOpenProduct,
}: {
  audit: ConsumerVoiceAudit;
  onCreateStrategy?: () => void;
  createStrategyLabel?: string;
  onOpenProduct?: (auditId: number) => void;
}) {
  const { report, subscores: sub } = audit;
  const subject = audit.kind === 'brand' ? audit.params.brand_name : audit.params.product_name || audit.url;
  const meta = (report && report.kind === 'product' ? (report as ConsumerVoiceProductReport).meta : null) || null;
  const demandMeta = meta && meta.demand;
  const oppMeta = meta && meta.opportunity;
  const renderBody = () => {
    if (audit.status === 'error') {
      return <p className="mt-6 text-[13px] text-[#c62828]">This analysis failed: {audit.generation_error || 'unknown error'}.</p>;
    }
    if (!report) return <p className="mt-6 text-[13px] text-muted">Report not available yet.</p>;
    if (report.kind === 'brand') return <BrandBody report={report as ConsumerVoiceBrandReport} onOpenProduct={onOpenProduct} />;
    return <ProductBody audit={audit} report={report as ConsumerVoiceProductReport} />;
  };
  return (
    <div className="px-6 py-6">
      <div className="text-[11px] font-bold uppercase tracking-[0.5px] text-accent-text">
        Consumer Voice {audit.kind === 'brand' ? 'Brand Analysis' : 'Analysis'}
      </div>
      <h2 className="text-[20px] font-bold text-text-1 mt-1 leading-tight">{subject}</h2>
      <div className="text-[12px] text-muted mt-1">
        {audit.kind === 'product' && audit.params.brand_name ? `${audit.params.brand_name} · ` : ''}
        {audit.params.category ? `${audit.params.category} · ` : ''}
        {audit.scored_at ? dayjs(audit.scored_at).format('MMM D, YYYY h:mm A') : ''}
        {audit.url ? (
          <>
            {' · '}
            <a href={audit.url} target="_blank" rel="noopener noreferrer" className="text-accent-text hover:underline">
              product page ↗
            </a>
          </>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-3 mt-5">
        <IndexCard
          label="Consumer Voice"
          value={audit.overall_score}
          caption={
            sub
              ? `Sentiment ${sub.sentiment ?? '—'} · Advocacy ${sub.advocacy ?? '—'} · Objection severity ${sub.objection_severity ?? '—'}`
              : 'How consumers feel: sentiment, advocacy, objection severity.'
          }
          placeholder="No usable consumer discussion found, so no score was assigned."
        />
        <IndexCard
          label="Consumer Demand"
          value={audit.demand_index}
          caption={componentsLine(demandMeta, { first_party_intent: 'first-party' }) || 'How much consumers search, ask and discuss.'}
          placeholder={
            demandMeta
              ? 'No search-demand signal came back for this run (Google Trends, Keyword Planner or Search Console).'
              : 'Needs a search-demand source — rerun the analysis to compute it.'
          }
        />
        <IndexCard
          label="Content Opportunity"
          value={audit.opportunity_index}
          caption={componentsLine(oppMeta) || 'Where demand and unanswered needs create an opportunity for content.'}
          placeholder={oppMeta ? 'Needs a demand signal — computed from demand × unanswered questions × objections × page gaps.' : 'Rerun the analysis to compute it.'}
        />
      </div>

      <ObjectiveScoreRow audit={audit} meta={meta} />

      {onCreateStrategy && audit.status === 'ready' ? (
        <div className="mt-4 border border-border-token rounded-xl p-4 bg-tint flex flex-wrap items-center justify-between gap-3">
          <div className="text-[13px] text-text-1">
            <strong>Turn this into a strategy.</strong> We’ll suggest the objective and secondary objective these shoppers’ words point to.
          </div>
          <button
            type="button"
            onClick={onCreateStrategy}
            className="text-[12px] font-semibold text-white bg-primary rounded-md px-3.5 py-2 cursor-pointer hover:opacity-90"
          >
            {createStrategyLabel}
          </button>
        </div>
      ) : null}

      {renderBody()}
    </div>
  );
}
